import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Eye, FileDown, FileCheck2, ShoppingCart, RotateCcw, Receipt } from "lucide-react";
import { ConvertExpenseDialog } from "@/components/expenses/ConvertExpenseDialog";
import { formatDOP, formatDate } from "@/lib/format";

const MOTIVOS = {
  1: "Error de especificación",
  2: "Firma digital inválida",
  3: "Envío duplicado",
  4: "RNC comprador no corresponde",
};

const EMPTY_FILTERS = { date_from: "", date_to: "", supplier: "", estado: "todos" };

async function downloadXml(url, filename) {
  try {
    const { data } = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(new Blob([data], { type: "application/xml" }));
    const a = document.createElement("a");
    a.href = u; a.download = filename; a.click();
    URL.revokeObjectURL(u);
  } catch (err) { toast.error(formatApiError(err)); }
}

export function EstadoBadge({ row }) {
  if (row.expense_id) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase bg-primary/15 text-primary" data-testid={`ecf-rec-estado-${row.id}`}>Gasto {row.expense_number}</span>;
  }
  if (row.purchase_id) {
    return <span className="text-[10px] px-1.5 py-0.5 rounded font-bold uppercase bg-primary/15 text-primary" data-testid={`ecf-rec-estado-${row.id}`}>Compra {row.purchase_number}</span>;
  }
  const ok = row.estado === 0;
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase ${ok ? "bg-success/15 text-success" : "bg-destructive/15 text-destructive"}`}
      title={ok ? "" : MOTIVOS[row.codigo_motivo] || ""}
      data-testid={`ecf-rec-estado-${row.id}`}
    >
      {ok ? "Recibido" : "No recibido"}
    </span>
  );
}

function Filters({ value, onChange, onReset }) {
  const set = (k) => (e) => onChange({ ...value, [k]: e?.target ? e.target.value : e });
  return (
    <div className="bg-card border border-border rounded-md p-4 mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end" data-testid="ecf-rec-filters">
      <div><Label className="overline">Desde</Label><Input type="date" value={value.date_from} onChange={set("date_from")} className="mt-1" data-testid="ecf-rec-filter-from" /></div>
      <div><Label className="overline">Hasta</Label><Input type="date" value={value.date_to} onChange={set("date_to")} className="mt-1" data-testid="ecf-rec-filter-to" /></div>
      <div><Label className="overline">Suplidor (RNC o nombre)</Label><Input value={value.supplier} onChange={set("supplier")} className="mt-1" placeholder="Buscar..." data-testid="ecf-rec-filter-supplier" /></div>
      <div>
        <Label className="overline">Estado</Label>
        <Select value={value.estado} onValueChange={set("estado")}>
          <SelectTrigger className="mt-1" data-testid="ecf-rec-filter-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            <SelectItem value="0">Recibido</SelectItem>
            <SelectItem value="1">No recibido</SelectItem>
            <SelectItem value="convertido">Convertido en compra</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Button variant="outline" onClick={onReset} data-testid="ecf-rec-filter-reset"><RotateCcw className="w-4 h-4 mr-1.5" /> Limpiar</Button>
    </div>
  );
}

function DownloadButtons({ row }) {
  const base = row.encf || row.id.slice(0, 8);
  return (
    <>
      <Button size="sm" variant="ghost" data-testid={`ecf-rec-xml-${row.id}`} title="Descargar e-CF (XML)"
        onClick={() => downloadXml(`/ecf-recibidos/${row.id}/xml`, `${row.rnc_emisor || ""}${base}.xml`)}>
        <FileDown className="w-4 h-4 mr-1" /> e-CF
      </Button>
      <Button size="sm" variant="ghost" data-testid={`ecf-rec-acuse-${row.id}`} title="Descargar acuse de recibo (ARECF)"
        onClick={() => downloadXml(`/ecf-recibidos/${row.id}/acuse`, `ARECF_${base}.xml`)}>
        <FileCheck2 className="w-4 h-4 mr-1" /> Acuse
      </Button>
    </>
  );
}

function DetailDialog({ id, onClose, onConvert, canConvert }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    if (id) api.get(`/ecf-recibidos/${id}`).then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ecf-rec-detail-dialog">
        <DialogHeader><DialogTitle>e-CF recibido {d?.encf}</DialogTitle></DialogHeader>
        {!d ? <div className="p-6 text-sm text-muted-foreground">Cargando…</div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <Info label="Suplidor" value={d.razon_social_emisor || "—"} testid="ecf-rec-detail-supplier" />
              <Info label="RNC emisor" value={d.rnc_emisor} mono />
              <Info label="Tipo e-CF" value={d.tipo_ecf || "—"} mono />
              <Info label="Fecha emisión" value={d.fecha_emision || "—"} mono />
              <Info label="Recibido" value={formatDate(d.received_at)} />
              <div><div className="overline">Estado</div><div className="mt-1"><EstadoBadge row={d} /></div>
                {d.estado === 1 && <div className="text-xs text-destructive mt-1" data-testid="ecf-rec-detail-motivo">{MOTIVOS[d.codigo_motivo]}</div>}
              </div>
            </div>
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="overline border-b border-border">
                  <tr><th className="text-left p-2">Descripción</th><th className="text-right p-2">Cant.</th><th className="text-right p-2">Precio</th><th className="text-right p-2">Monto</th></tr>
                </thead>
                <tbody>
                  {d.items.map((it, i) => (
                    <tr key={i} className="border-b border-border/60">
                      <td className="p-2">{it.name}{!it.itbis_applies && <span className="ml-2 text-[10px] text-muted-foreground uppercase">Exento</span>}</td>
                      <td className="p-2 text-right data-cell">{it.quantity}</td>
                      <td className="p-2 text-right data-cell">{formatDOP(it.price)}</td>
                      <td className="p-2 text-right data-cell">{formatDOP(it.monto || it.quantity * it.price)}</td>
                    </tr>
                  ))}
                  {d.items.length === 0 && <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">Sin líneas</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-6 text-sm">
              <span>ITBIS: <b className="data-cell">{formatDOP(d.total_itbis)}</b></span>
              <span>Total: <b className="data-cell" data-testid="ecf-rec-detail-total">{formatDOP(d.monto_total)}</b></span>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          {d && <DownloadButtons row={d} />}
          {d && canConvert && d.estado === 0 && !d.purchase_id && (
            <Button className="bg-primary font-bold" onClick={() => onConvert(d)} data-testid="ecf-rec-detail-convert">
              <ShoppingCart className="w-4 h-4 mr-1.5" /> Convertir en compra
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, mono, testid }) {
  return <div><div className="overline">{label}</div><div className={`mt-1 ${mono ? "font-mono" : "font-semibold"}`} data-testid={testid}>{value}</div></div>;
}

export default function EcfRecibidos() {
  const { can } = useAuth();
  const confirm = useConfirm();
  const canConvert = can ? can("purchases.edit") : true;
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [detailId, setDetailId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([k, v]) => v && !(k === "estado" && v === "todos")));
    try { setRows((await api.get("/ecf-recibidos", { params })).data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const [expenseFor, setExpenseFor] = useState(null);
  const convert = async (row) => {
    const ok = await confirm({
      title: `Convertir ${row.encf} en compra de mercancía`,
      description: "Se registrará una compra a crédito con las líneas del e-CF. Los productos que coincidan por código o nombre sumarán existencia. Si no es mercancía (servicios, oficina, reparaciones), usa \"Gasto\".",
      confirmLabel: "Convertir",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/ecf-recibidos/${row.id}/convert`);
      toast.success(`Compra ${data.purchase_number} registrada${data.unmatched_items ? ` · ${data.unmatched_items} línea(s) sin producto en inventario` : ""}`);
      setDetailId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="e-CF Recibidos"
        subtitle="Comprobantes electrónicos que tus suplidores te envían, con su acuse de recibo."
        testid="ecf-recibidos-header"
        actions={<Link to="/purchases"><Button variant="outline" data-testid="ecf-rec-go-purchases"><ShoppingCart className="w-4 h-4 mr-1.5" /> Ver compras</Button></Link>}
      />
      <Filters value={filters} onChange={setFilters} onReset={() => setFilters(EMPTY_FILTERS)} />
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="ecf-rec-table">
          <thead className="overline border-b border-border">
            <tr>
              <th className="text-left p-3">e-NCF</th>
              <th className="text-left p-3">Suplidor</th>
              <th className="text-left p-3">Recibido</th>
              <th className="text-right p-3">Total</th>
              <th className="text-left p-3">Estado</th>
              <th className="text-right p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/60 hover:bg-accent transition-colors" data-testid={`ecf-rec-row-${r.id}`}>
                <td className="p-3 data-cell font-bold">{r.encf || "—"}</td>
                <td className="p-3"><div className="font-semibold">{r.razon_social_emisor || "—"}</div><div className="text-xs font-mono text-muted-foreground">{r.rnc_emisor}</div></td>
                <td className="p-3 text-xs text-muted-foreground">{formatDate(r.received_at)}</td>
                <td className="p-3 text-right data-cell font-bold">{formatDOP(r.monto_total)}</td>
                <td className="p-3"><EstadoBadge row={r} /></td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end items-center">
                    {canConvert && r.estado === 0 && !r.purchase_id && !r.expense_id ? (
                      <>
                        <Button size="sm" variant="ghost" className="text-primary w-[92px]" onClick={() => convert(r)} data-testid={`ecf-rec-convert-${r.id}`}>
                          <ShoppingCart className="w-4 h-4 mr-1" /> Compra
                        </Button>
                        <Button size="sm" variant="ghost" className="text-primary w-[80px]" onClick={() => setExpenseFor(r)} data-testid={`ecf-rec-expense-${r.id}`}>
                          <Receipt className="w-4 h-4 mr-1" /> Gasto
                        </Button>
                      </>
                    ) : canConvert && <span className="inline-block w-[172px]" />}
                    <Button size="icon" variant="ghost" title="Ver detalle" onClick={() => setDetailId(r.id)} data-testid={`ecf-rec-view-${r.id}`}><Eye className="w-4 h-4" /></Button>
                    <DownloadButtons row={r} />
                  </div>
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-muted-foreground" data-testid="ecf-rec-empty">Aún no has recibido comprobantes electrónicos de suplidores.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <DetailDialog id={detailId} onClose={() => setDetailId(null)} onConvert={convert} canConvert={canConvert} />
      {expenseFor && <ConvertExpenseDialog row={expenseFor} onClose={() => setExpenseFor(null)} onDone={() => { setExpenseFor(null); load(); }} />}
    </PageContainer>
  );
}
